import React, { useState, useMemo, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./gbpCategories.css";

import frData from "./data/fr.json";
import enUSData from "./data/en-US.json";
import enGBData from "./data/en-GB.json";
import esData from "./data/es.json";
import itData from "./data/it.json";

const LANGUAGES = [
  { key: "fr", label: "Français", flag: "🇫🇷", data: frData },
  { key: "en-US", label: "English (US)", flag: "🇺🇸", data: enUSData },
  { key: "en-GB", label: "English (UK)", flag: "🇬🇧", data: enGBData },
  { key: "es", label: "Español", flag: "🇪🇸", data: esData },
  { key: "it", label: "Italiano", flag: "🇮🇹", data: itData },
];

const PAGE_SIZE = 50;

const GbpCategories = () => {
  const [search, setSearch] = useState("");
  const [selectedLang, setSelectedLang] = useState("fr");
  const [copiedId, setCopiedId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const toolRef = useRef(null);

  const scrollToTool = () => {
    toolRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const currentLang = LANGUAGES.find((l) => l.key === selectedLang);
  const categories = currentLang?.data || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return categories;
    return categories.filter(
      (c) =>
        c.category.toLowerCase().includes(q) ||
        c.gcid.toLowerCase().includes(q),
    );
  }, [search, categories]);

  const displayed = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount],
  );

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const handleCopy = useCallback(async (text, gcid) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(gcid);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      /* fallback silently */
    }
  }, []);

  const loadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <div className="gbp-page">
      <Helmet>
        <title>
          Recherche de catégories Google Business Profile | Outil gratuit GBP
        </title>
        <meta
          name="description"
          content="Trouvez et copiez les catégories Google Business Profile (GBP) en français, anglais, espagnol et italien. Outil gratuit pour optimiser votre fiche Google Maps."
        />
        <link rel="canonical" href="https://localranker.tech/google-business-profile-categories" />
        <meta property="og:title" content="Recherche de catégories Google Business Profile | Outil gratuit GBP" />
        <meta property="og:description" content="Trouvez et copiez les catégories Google Business Profile (GBP) en français, anglais, espagnol et italien." />
        <meta property="og:url" content="https://localranker.tech/google-business-profile-categories" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>

      <Hero
        title="Recherche de catégories Google Business Profile"
        subtitle="Trouvez la catégorie GBP parfaite pour votre fiche Google. Recherchez par nom ou identifiant dans 5 langues."
        ctaText="Rechercher une catégorie"
        ctaOnClick={scrollToTool}
      />

      <div className="gbp" ref={toolRef}>
        <div className="gbp__card">
          <div className="gbp__controls">
            <div className="gbp__search-wrap">
              <svg
                className="gbp__search-icon"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                  stroke="currentColor"
                  strokeWidth="1.66"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                className="gbp__search"
                type="text"
                placeholder="Rechercher une catégorie… ex: restaurant, coiffeur, plombier"
                value={search}
                onChange={handleSearch}
                autoFocus
              />
              {search && (
                <button
                  className="gbp__search-clear"
                  onClick={() => {
                    setSearch("");
                    setVisibleCount(PAGE_SIZE);
                  }}
                  aria-label="Effacer la recherche"
                >
                  ×
                </button>
              )}
            </div>

            <div className="gbp__lang-row">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.key}
                  className={`gbp__lang${
                    selectedLang === lang.key ? " gbp__lang--active" : ""
                  }`}
                  onClick={() => {
                    setSelectedLang(lang.key);
                    setVisibleCount(PAGE_SIZE);
                  }}
                >
                  <span className="gbp__lang-flag">{lang.flag}</span>
                  <span className="gbp__lang-label">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="gbp__meta">
            <span className="gbp__count">
              {filtered.length.toLocaleString("fr-FR")} catégorie
              {filtered.length !== 1 ? "s" : ""}
              {search && " trouvée" + (filtered.length !== 1 ? "s" : "")}
            </span>
          </div>

          <div className="gbp__list">
            <div className="gbp__list-header">
              <span className="gbp__col-name">Catégorie</span>
              <span className="gbp__col-id">Identifiant (gcid)</span>
              <span className="gbp__col-action"></span>
            </div>
            {displayed.length === 0 && (
              <div className="gbp__empty">
                <span className="gbp__empty-icon">🔍</span>
                <p>Aucune catégorie trouvée pour «&nbsp;{search}&nbsp;»</p>
                <p className="gbp__empty-hint">
                  Essayez un terme plus court ou vérifiez l'orthographe.
                </p>
              </div>
            )}
            {displayed.map((cat) => (
              <div className="gbp__row" key={cat.gcid}>
                <span className="gbp__col-name" title={cat.category}>
                  {cat.category}
                </span>
                <span className="gbp__col-id">
                  <code>{cat.gcid}</code>
                </span>
                <span className="gbp__col-action">
                  <button
                    className={`gbp__copy${
                      copiedId === cat.gcid ? " gbp__copy--done" : ""
                    }`}
                    onClick={() => handleCopy(cat.category, cat.gcid)}
                    title="Copier le nom de la catégorie"
                  >
                    {copiedId === cat.gcid ? (
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.6666 5L7.49998 14.1667L3.33331 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="7"
                          y="7"
                          width="10"
                          height="10"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M13 7V5C13 3.89543 12.1046 3 11 3H5C3.89543 3 3 3.89543 3 5V11C3 12.1046 3.89543 13 5 13H7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <span>{copiedId === cat.gcid ? "Copié" : "Copier"}</span>
                  </button>
                </span>
              </div>
            ))}
          </div>

          {visibleCount < filtered.length && (
            <div className="gbp__load-more">
              <button className="gbp__load-more-btn" onClick={loadMore}>
                Afficher plus de catégories ({filtered.length - visibleCount}{" "}
                restantes)
              </button>
            </div>
          )}
        </div>
      </div>

      <section className="gbp-seo">
        <div className="gbp-seo__inner">
          <article className="gbp-seo__block">
            <h2>
              Qu'est-ce que l'outil de recherche de catégories Google Business
              Profile ?
            </h2>
            <p>
              Cet outil génère la{" "}
              <strong>
                liste complète des catégories Google Business Profile
              </strong>{" "}
              (les catégories utilisées pour les entreprises sur Google Maps).
              Vous pouvez générer la liste dans différentes combinaisons de
              langues et de pays. Les catégories par défaut dans GBP sont en
              anglais avec le pays États-Unis. Ces catégories sont exactement
              les mêmes que celles que vous voyez dans Google Maps.
            </p>
            <div className="gbp-seo__stats-row">
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">
                  {categories.length.toLocaleString("fr-FR")}+
                </span>
                <span className="gbp-seo__stat-text">
                  catégories disponibles dans Google Business Profile
                </span>
              </div>
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">5</span>
                <span className="gbp-seo__stat-text">
                  langues supportées : FR, EN-US, EN-GB, ES, IT
                </span>
              </div>
              <div className="gbp-seo__stat-card">
                <span className="gbp-seo__stat-number">10</span>
                <span className="gbp-seo__stat-text">
                  nombre maximum de catégories pour une fiche GBP
                </span>
              </div>
            </div>
          </article>

          <article className="gbp-seo__block">
            <h2>Pourquoi les catégories GBP sont-elles si importantes ?</h2>
            <p>
              Après le <strong>nom de votre entreprise</strong>, la{" "}
              <strong>catégorie principale</strong> est le facteur le plus
              important pour un meilleur classement dans les résultats de
              recherche locale Google. Choisir la bonne catégorie détermine en
              grande partie pour quelles requêtes votre fiche apparaît dans le
              Local Pack et Google Maps.
            </p>
            <p>
              Une catégorie mal choisie peut littéralement rendre votre fiche
              invisible sur des dizaines de mots-clés pertinents. À l'inverse,
              une catégorie bien ciblée booste votre visibilité immédiatement.
            </p>
          </article>

          <article className="gbp-seo__block">
            <h2>
              Bonnes pratiques pour choisir vos catégories Google Business
              Profile
            </h2>
            <div className="gbp-seo__tips">
              <div className="gbp-seo__tip">
                <span className="gbp-seo__tip-icon">🎯</span>
                <div>
                  <strong>
                    Choisissez la catégorie principale la plus spécifique
                  </strong>
                  <p>
                    Par exemple, si vous êtes un restaurant italien, choisissez
                    «&nbsp;Restaurant italien&nbsp;» plutôt que simplement
                    «&nbsp;Restaurant&nbsp;». Plus c'est précis, mieux Google
                    comprend votre activité.
                  </p>
                </div>
              </div>
              <div className="gbp-seo__tip">
                <span className="gbp-seo__tip-icon">➕</span>
                <div>
                  <strong>
                    Ajoutez 2 à 3 catégories secondaires pertinentes
                  </strong>
                  <p>
                    Complétez votre catégorie principale avec des catégories
                    additionnelles qui décrivent d'autres aspects de votre
                    activité. Mais ne dépassez jamais 5 à 6 catégories au total.
                  </p>
                </div>
              </div>
              <div className="gbp-seo__tip">
                <span className="gbp-seo__tip-icon">⚠️</span>
                <div>
                  <strong>N'abusez pas des catégories</strong>
                  <p>
                    Le nombre maximum de catégories est de 10, mais ajouter des
                    catégories non pertinentes peut diluer votre signal et nuire
                    à votre classement. Privilégiez la qualité à la quantité.
                  </p>
                </div>
              </div>
              <div className="gbp-seo__tip">
                <span className="gbp-seo__tip-icon">🔄</span>
                <div>
                  <strong>
                    Surveillez régulièrement les nouvelles catégories
                  </strong>
                  <p>
                    Google ajoute et supprime des catégories chaque mois. Gardez
                    un œil sur les nouvelles catégories qui pourraient mieux
                    décrire votre activité.
                  </p>
                </div>
              </div>
              <div className="gbp-seo__tip">
                <span className="gbp-seo__tip-icon">📊</span>
                <div>
                  <strong>Testez et mesurez l'impact</strong>
                  <p>
                    Ajoutez ou supprimez des catégories secondaires et observez
                    comment votre classement et vos leads évoluent. C'est une
                    bonne pratique de A/B tester vos catégories.
                  </p>
                </div>
              </div>
            </div>
          </article>

          <article className="gbp-seo__block">
            <h2>Combien de catégories devez-vous ajouter à votre fiche ?</h2>
            <div className="gbp-seo__scale">
              <div className="gbp-seo__scale-zone gbp-seo__scale-zone--bad">
                <span className="gbp-seo__scale-range">1 seule</span>
                <span className="gbp-seo__scale-label">Insuffisant</span>
              </div>
              <div className="gbp-seo__scale-zone gbp-seo__scale-zone--good">
                <span className="gbp-seo__scale-range">3 – 5</span>
                <span className="gbp-seo__scale-label">Idéal</span>
              </div>
              <div className="gbp-seo__scale-zone gbp-seo__scale-zone--ok">
                <span className="gbp-seo__scale-range">6 – 8</span>
                <span className="gbp-seo__scale-label">Acceptable</span>
              </div>
              <div className="gbp-seo__scale-zone gbp-seo__scale-zone--sus">
                <span className="gbp-seo__scale-range">9 – 10</span>
                <span className="gbp-seo__scale-label">Risqué</span>
              </div>
            </div>
            <p>
              La meilleure pratique est de choisir la catégorie la plus
              pertinente comme catégorie principale et d'ajouter{" "}
              <strong>2 à 3 catégories additionnelles</strong> qui décrivent
              également votre activité, sans dépasser 5 à 6 catégories au total.
              Ajouter trop de catégories non pertinentes peut envoyer des
              signaux confus à Google et diluer votre pertinence pour vos
              requêtes clés.
            </p>
          </article>

          <article className="gbp-seo__block">
            <h2>Comment les catégories GBP impactent votre SEO local ?</h2>
            <div className="gbp-seo__factors">
              <div className="gbp-seo__factor">
                <span className="gbp-seo__factor-icon">📍</span>
                <strong>Visibilité Local Pack</strong>
                <p>
                  La catégorie principale détermine dans quel Local Pack votre
                  fiche apparaît.
                </p>
              </div>
              <div className="gbp-seo__factor">
                <span className="gbp-seo__factor-icon">🔍</span>
                <strong>Requêtes de découverte</strong>
                <p>
                  Les catégories définissent pour quelles recherches génériques
                  votre fiche est éligible.
                </p>
              </div>
              <div className="gbp-seo__factor">
                <span className="gbp-seo__factor-icon">🏆</span>
                <strong>Avantage concurrentiel</strong>
                <p>
                  Une catégorie plus spécifique que vos concurrents vous donne
                  un avantage de classement.
                </p>
              </div>
            </div>
            <p>
              Google utilise vos catégories comme signal principal pour
              déterminer la pertinence de votre fiche face à une requête. Un
              choix stratégique de catégories est donc{" "}
              <strong>indispensable</strong> pour toute stratégie de SEO local
              efficace.
            </p>
          </article>

          <article className="gbp-seo__block">
            <h2>FAQ — Catégories Google Business Profile</h2>
            <div className="gbp-seo__faq">
              <div className="gbp-seo__faq-item">
                <h3>Peut-on changer sa catégorie principale GBP ?</h3>
                <p>
                  Oui, vous pouvez modifier votre catégorie principale à tout
                  moment depuis votre tableau de bord Google Business Profile.
                  Attention cependant : un changement de catégorie principale
                  peut impacter temporairement votre classement le temps que
                  Google réindexe votre fiche.
                </p>
              </div>
              <div className="gbp-seo__faq-item">
                <h3>
                  À quelle fréquence Google met-il à jour les catégories ?
                </h3>
                <p>
                  Google ajoute et supprime des catégories tous les mois. Il est
                  important de vérifier régulièrement si de nouvelles catégories
                  plus pertinentes sont disponibles pour votre activité.
                </p>
              </div>
              <div className="gbp-seo__faq-item">
                <h3>Ma catégorie n'existe pas dans la liste, que faire ?</h3>
                <p>
                  Google ne permet pas de créer des catégories personnalisées.
                  Choisissez la catégorie existante qui décrit le mieux votre
                  activité principale. Vous pouvez compléter avec des catégories
                  secondaires plus larges.
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default GbpCategories;
